import { inject, Injectable } from '@angular/core';
import { fromEvent, filter } from 'rxjs';
import { AuthStore } from '@auth/stores/auth.store';

const REFRESH_TTL_MS = 15 * 60 * 1000; // 15 min de inactividad activa

/**
 * Servicio de refresco de permisos en runtime.
 *
 * Fuentes de refresco:
 * 1. `visibilitychange` — cuando el usuario vuelve al tab (cross-tab awareness).
 * 2. `forceRefresh()` — llamado manualmente por stores después de mutaciones IAM.
 * 3. TTL silencioso — si han pasado más de 15 min desde el último refresco.
 *
 * También detecta logout en otro tab vía `storage` event.
 *
 * No genera peticiones innecesarias: respeta el TTL entre refrescos automáticos.
 */
@Injectable({ providedIn: 'root' })
export class PermissionRefreshService {
  private readonly authStore = inject(AuthStore);
  private lastRefreshAt = 0;

  constructor() {
    // Refresco al volver al tab
    fromEvent(document, 'visibilitychange')
      .pipe(filter(() => !document.hidden))
      .subscribe(() => this.tryRefresh());

    // Sincronización de logout entre tabs
    fromEvent<StorageEvent>(window, 'storage')
      .pipe(filter((e) => e.key === 'authToken' && e.newValue === null))
      .subscribe(() => this.authStore.logout());
  }

  /**
   * Fuerza un refresco inmediato ignorando el TTL.
   * Llamar desde stores de configuración después de modificar permisos/perfiles.
   */
  forceRefresh(): void {
    this.lastRefreshAt = 0;
    this.tryRefresh();
  }

  private tryRefresh(): void {
    if (!this.authStore.isAuthenticated()) return;
    const now = Date.now();
    if (now - this.lastRefreshAt < REFRESH_TTL_MS) return;
    this.lastRefreshAt = now;
    this.authStore.refreshMenu();
  }
}
